import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  Users,
  Calendar,
  X,
  RefreshCw,
  Utensils,
  Hotel,
} from "lucide-react";
import Button from "./button";
import { Card } from "./card";
import { format } from "date-fns";
import "./notificationSystem.css";
import { getList } from "../../services/userServices";
import { ENDPOINT, ERROR_ACTIONS } from "../../config/constants";
import { useAuth } from "../../hooks/useAuth";
import { PAGE_SIZE } from "../../config/config";
import {
  Box,
  Grid,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Encrypt } from "../../Crypto-Helper";
import { useDispatch } from "react-redux";
import { setExternalUserNotificationData } from "../../redux/slices/userSlice";
import { toast } from "react-toastify";
import { setCustomerReservationData } from "../../redux/slices/masterDataSlice";
import Logo from "../../assets/images/Datalekt_Dots.svg";

const NotificationItem = ({ notification, index, onReserve, onCancel }) => {
  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? "pm" : "am";
    const formattedHours = hours % 12 || 12;
    return {
      date: `${day} ${month} ${year}`,
      time: `${formattedHours}:${minutes
        .toString()
        .padStart(2, "0")} ${period}`,
    };
  };

  const dateTime = formatDateTime(notification?.reservationTime);

  return (
    <motion.div
      key={notification.id}
      className="notification-item"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        ease: "easeOut",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="notification-header">
        <h4 className="notification-title" style={{ color: "#0C364F" }}>
          Booking - {notification?.name}
        </h4>
      </div>

      <div className="notification-restaurant" style={{ color: "#41B3CB" }}>
        {notification?.restaurantName}
      </div>

      <div className="notification-meta">
        <Calendar className="icon" />
        <span>{dateTime.date}</span>
        <span>•</span>
        <span>{dateTime.time}</span>
        <span>•</span>
        <Users className="icon" />
        <span>
          {notification?.peopleCount}{" "}
          {notification?.peopleCount === 1 ? "Guest" : "Guests"}
        </span>
      </div>

      <div className="notification-actions">
        <Button
          variant="outline"
          size="sm"
          className="btn-cancel"
          onClick={(e) => {
            e.stopPropagation();
            onCancel(notification?.id);
          }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="btn-confirm"
          onClick={() => onReserve(notification)}
        >
          Reserve
        </Button>
      </div>
    </motion.div>
  );
};

const NotificationBell = ({ unreadCount, bellAnimation, onClick, bellRef }) => (
  <motion.div
    ref={bellRef}
    className="bell-wrapper"
    animate={{
      rotate: bellAnimation ? [0, 15, -15, 10, -10, 5, -5, 0] : 0,
    }}
    transition={{ duration: 0.6, ease: "easeInOut" }}
    whileHover={{ scale: 1.1 }}
    onClick={(event) => event.stopPropagation()}
  >
    <Button
      variant="outline"
      size="icon"
      className="bell-button"
      onClick={(event) => onClick(event)}
    >
      <Bell style={{ height: "2rem" }} className="bell-icon" />
      {unreadCount > 0 && <BadgeAnimation unreadCount={unreadCount} />}
      <div className="bell-hover-glow" />
    </Button>
  </motion.div>
);

const BadgeAnimation = ({ unreadCount }) => {
  const animationTypes = ["bounce", "spotlight", "pulse", "shake"];
  const [animationType, setAnimationType] = useState(
    () => animationTypes[Math.floor(Math.random() * animationTypes.length)]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationType(
        animationTypes[Math.floor(Math.random() * animationTypes.length)]
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);


  const getBounceAnimation = () => ({
    y: [0, -8, 0, -6, 0],
    scale: [1, 1.2, 1, 1.1, 1],
    transition: {
      duration: 1.2,
      repeat: Infinity,
      repeatDelay: 1.5 + Math.random() * 1.5,
    },
  });

  const getSpotlightAnimation = () => ({
    boxShadow: [
      "0 2px 6px rgba(65, 179, 203, 0.5)",
      "0 0 12px 4px rgba(65, 179, 203, 0.8)",
      "0 2px 6px rgba(65, 179, 203, 0.5)",
    ],
    scale: [1, 1.15, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatDelay: 2,
    },
  });

  const getPulseAnimation = () => ({
    scale: [1, 1.3, 1],
    backgroundColor: ["#41B3CB", "rgba(65, 179, 203, 0.8)", "#41B3CB"],
    transition: {
      duration: 1.8,
      repeat: Infinity,
      repeatDelay: 1,
    },
  });

  const getShakeAnimation = () => ({
    x: [0, 3, -3, 3, -3, 0],
    transition: {
      duration: 0.5,
      repeat: Infinity,
      repeatDelay: 2.5,
    },
  });

  const getAnimationProps = () => {
    switch (animationType) {
      case "bounce":
        return getBounceAnimation();
      case "spotlight":
        return getSpotlightAnimation();
      case "pulse":
        return getPulseAnimation();
      case "shake":
        return getShakeAnimation();
      default:
        return getBounceAnimation();
    }
  };

  const [hasEntered, setHasEntered] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setHasEntered(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.span
      className="badge-animation"
      style={{
        zIndex: 10,
      }}
      initial={{ scale: 0 }}
      animate={
        hasEntered
          ? getAnimationProps()
          : {
              scale: [0, 1.3, 1],
              y: [0, -5, 0],
            }
      }
      transition={!hasEntered ? { duration: 0.5 } : undefined}
    >
      {unreadCount}
    </motion.span>
  );
};

const BellPopup = ({ unreadCount }) => {
  const isMobile = window.innerWidth < 480;

  return (
    <motion.div
      initial={{ width: 0, opacity: 0, x: isMobile ? 20 : -20 }}
      animate={{ width: "auto", opacity: 1, x: 0 }}
      exit={{ width: 0, opacity: 0, x: isMobile ? 20 : -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="bell-popup"
    >
      <div className="bell-popup-content">
        <span className="bell-popup-text">
          You have {unreadCount} new reservation{unreadCount !== 1 ? "s" : ""}
        </span>
      </div>
    </motion.div>
  );
};

const EmptyNotificationState = () => (
  <div className="empty-notification-state">
    <Bell className="empty-notification-icon" />
    <p>No notifications yet</p>
  </div>
);

const NotificationCard = ({
  notifications,
  totalNotificationCount,
  currentPage,
  totalPageCount,
  onPageChange,
  onClose,
  onRefresh,
  onReserve,
  onCancel,
}) => (
  <Card
    className="notification-card"
    onClick={(event) => event.stopPropagation()}
  >
    <div
      className="notification-card-header"
      onClick={(event) => event.stopPropagation()}
    >
      <div>
        <h3 className="notification-card-title">Notifications</h3>
        {totalNotificationCount > 0 && (
          <p className="notification-card-subtitle">
            {totalNotificationCount} notification
            {totalNotificationCount !== 1 ? "s" : ""}
          </p>
        )}
      </div>
      <div style={{ display: "flex", gap: "12px" }}>
        <RefreshCw 
          className="refresh-icon" 
          strokeWidth={2} 
          onClick={(event) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            onRefresh(event);
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
          }}
          style={{ cursor: 'pointer' }}
        />
        <X 
          className="close-icon" 
          strokeWidth={2} 
          onClick={(event) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            onClose();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
          }}
          style={{ cursor: 'pointer' }}
        />
      </div>
    </div>

    <div
      className="notification-card-body"
      onClick={(event) => event.stopPropagation()}
    >
      {notifications?.length === 0 ? (
        <EmptyNotificationState />
      ) : (
        <>
          {notifications?.map((notification, index) => (
            <NotificationItem
              key={notification.id || index}
              notification={notification}
              index={index}
              onReserve={onReserve}
              onCancel={onCancel}
            />
          ))}
        </>
      )}
    </div>
    {totalPageCount > 0 && notifications?.length > 0 && (
      <div className="pagination-wrapper">
        <Box
          className="pagination-container"
          sx={{
            position: { xs: "fixed", sm: "absolute" },
            bottom: 0,
            left: 0,
            right: 0,
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            width: "100%",
            padding: { xs: "10px 0", sm: "15px 0" },
            backgroundColor: "#f8f9fa",
            boxShadow: "0 -4px 10px rgba(0,0,0,0.15)",
            zIndex: 1000,
            borderTop: "1px solid #e0e0e0",
            borderRadius: { xs: 0, sm: "0 0 1rem 1rem" },
          }}
          onClick={(event) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
          }}
        >
          <Pagination
            count={totalPageCount}
            variant="outlined"
            shape="rounded"
            color="primary"
            page={currentPage}
            size={window.innerWidth < 480 ? "large" : "medium"}
            siblingCount={window.innerWidth < 480 ? 0 : 1}
            boundaryCount={window.innerWidth < 480 ? 1 : 1}
            sx={{
              "& .MuiPagination-ul": {
                flexWrap: "nowrap",
                justifyContent: "center",
              },
              "& .MuiPaginationItem-root": {
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                fontWeight: 500,
                margin: { xs: "0 4px", sm: "0 2px" },
                minWidth: { xs: "40px", sm: "36px" },
                height: { xs: "40px", sm: "36px" },
                fontSize: { xs: "1rem", sm: "inherit" },
              },
              "& .Mui-selected": {
                backgroundColor: "#0C364F !important",
                color: "white !important",
                fontWeight: "bold",
              },
            }}
            onChange={(event, value) => {
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              onPageChange(event, value);
            }}
            onClick={(event) => {
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
            }}
          />
        </Box>
      </div>
    )}
  </Card>
);

const NotificationPopup = ({ unreadCount, onClose, openCounter }) => (
  <motion.div
    initial={{ y: 100, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    exit={{ y: 100, opacity: 0 }}
    transition={{ type: "spring", stiffness: 300, damping: 30 }}
    className="notification-popup"
    onClick={(e) => e.stopPropagation()}
  >
    {card(onClose, unreadCount)}
  </motion.div>
);

const card = (onClose, unreadCount) => {
  return (
    <Card className="notification-card-popup card1" onClick={(e) => e.stopPropagation()}>
      <div className="popup-close-wrapper">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClose(e);
          }}
          variant="ghost"
          size="sm"
          className="popup-close-button"
        >
          <X className="popup-close-icon" />
        </Button>
      </div>
      {/* <div className="popup-card2-wrapper">
        <img src={Logo}  style={{ height: "14rem" }} />
      </div> */}
      <div className="popup-content">
        {/* <br />
        <br /> */}
        <br />
        <div className="popup-title-wrapper">
          <h4 className="popup-title">Busy day ahead!</h4>
        </div>
        <p className="popup-text">
          You have <strong>{unreadCount}</strong> new reservations waiting for
          confirmation
        </p>

        <div className="popup-actions">
          <div className="popup-action-item">
            <Calendar className="popup-action-icon" />
            <span className="popup-action-text">Today</span>
          </div>
          <div className="popup-action-item">
            <Utensils className="popup-action-icon" />
            <span className="popup-action-text">{unreadCount} Reservations</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default function NotificationSystem() {
  const { logout } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [isRefresh, setRefresh] = useState(0);
  const [bellAnimation, setBellAnimation] = useState(false);
  const bellRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [totalNotificationCount, setNotificationCount] = useState(0);
  const isMobile = useMediaQuery("(max-width: 480px)");
  const unreadCount = totalNotificationCount;
  const [showPopup, setShowPopup] = useState(true);
  const [openCounter, setOpenCounter] = useState(0);


  useEffect(() => {
    if (unreadCount > 0 && !isOpen) {
      setBellAnimation(true);
      setTimeout(() => setBellAnimation(false), 1000);
    }
  }, [unreadCount, isOpen]);
  
  useEffect(() => {
    const hasShownPopup = localStorage.getItem("hasShownPopupAfterLogin");

    if (unreadCount > 3 && hasShownPopup === "false") {
      setShowPopup(true);
      localStorage.setItem("hasShownPopupAfterLogin", "true");
    } else {
      setShowPopup(false);
    }
  }, [unreadCount]);
  
  useEffect(() => {
    const hasShownPopup = localStorage.getItem("hasShownPopupAfterLogin");
    if (hasShownPopup === null) {
      localStorage.setItem("hasShownPopupAfterLogin", "false");
    }
  }, []);

  const closePopup = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault && e.preventDefault();
    }
    setShowPopup(false);
  };
  const getNotificationsList = async () => {
    const payload = new FormData();
    const previousCount = currentPage * PAGE_SIZE - PAGE_SIZE;
    payload.append("previousCount", previousCount);

    const resp = await getList("POST", ENDPOINT.NOTIFICATION_LIST, payload);
    if (!resp?.error) {
      setNotifications(resp?.data?.data);
      setNotificationCount(resp?.data?.totalRecordsCount);
      setTotalPageCount(
        (resp?.data?.totalRecordsCount || 0) > PAGE_SIZE
          ? Math.ceil((resp?.data?.totalRecordsCount || 0) / PAGE_SIZE)
          : 0
      );
    } else if (resp?.error === ERROR_ACTIONS.SESSION_EXPIRED) {
      logout();
    }
  };

  useEffect(() => {
    if (isOpen) {
      getNotificationsList();
    }
  }, [isOpen, isRefresh, currentPage]);

  // This useEffect was removed as it's now handled by the unreadCount useEffect

  useEffect(() => {
    // Initial fetch to get notification count
    getNotificationsList();

    // Set up polling for notifications
    const interval = setInterval(() => {
      if (!isOpen) {
        getNotificationsList();
      }
    }, 30000); // Poll every 30 seconds when closed

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on pagination, refresh button, notification card or popup
      const isPaginationClick = event.target.closest(".pagination-container");
      const isRefreshClick = event.target.closest(".notification-card-refresh");
      const isNotificationCardClick = event.target.closest(".notification-card");
      const isNotificationPopupClick = event.target.closest(".notification-popup") || 
                                      event.target.closest(".notification-card-popup") ||
                                      event.target.closest(".popup-close-button");

      if (
        bellRef.current &&
        !bellRef.current.contains(event.target) &&
        !isPaginationClick &&
        !isRefreshClick &&
        !isNotificationCardClick &&
        !isNotificationPopupClick
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleNotifications = (event) => {
    if (event) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }
    setIsOpen(!isOpen);
  };

  const handlePageChange = (event, value) => {
    if (event) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }
    setCurrentPage(value);
  };

  const refreshNotifications = (event) => {
    if (event) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }
    setRefresh((prev) => prev + 1);
  };

  const reserve = (notification) => {
    dispatch(setCustomerReservationData(null));
    dispatch(setExternalUserNotificationData(notification));
    navigate(`/reservation/${Encrypt(notification?.customerId)}`);
    setIsOpen(false);
  };

  const decline = (id) => {
    setSelectedNotificationId(id);
    setDialogOpen(true);
  };

  const handleDeclineConfirm = async () => {
    const payload = new FormData();
    payload.append("id", selectedNotificationId);
    const response = await getList(
      "POST",
      ENDPOINT.CANCEL_RESERVATION,
      payload
    );
    if (response?.data?.status === 200) {
      toast.success(response?.data?.message);

      const updatedTotalNotificationCount = totalNotificationCount - 1;
      const updatedTotalPageCount = Math.ceil(
        updatedTotalNotificationCount / PAGE_SIZE
      );

      if (currentPage > updatedTotalPageCount && updatedTotalPageCount > 0) {
        setCurrentPage(updatedTotalPageCount);
      }

      setDialogOpen(false);
      setRefresh((prev) => prev + 1);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedNotificationId(null);
  };

  return (
    <div
      className="notification-system-container"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="notification-bell-wrapper"
        onClick={(event) => event.stopPropagation()}
      >
        <NotificationBell
          unreadCount={totalNotificationCount}
          bellAnimation={bellAnimation}
          onClick={toggleNotifications}
          bellRef={bellRef}
        />

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="dropdown-absolute"
              onClick={(event) => event.stopPropagation()}
            >
              <NotificationCard
                notifications={notifications}
                totalNotificationCount={totalNotificationCount}
                currentPage={currentPage}
                totalPageCount={totalPageCount}
                onPageChange={handlePageChange}
                onClose={toggleNotifications}
                onRefresh={refreshNotifications}
                onReserve={reserve}
                onCancel={decline}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {bellAnimation && !isOpen && !isMobile && (
            <BellPopup unreadCount={totalNotificationCount} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showPopup && unreadCount > 3 && (
            <div onClick={(e) => e.stopPropagation()} className="notification-popup-wrapper">
              <NotificationPopup
                unreadCount={unreadCount}
                onClose={closePopup}
                openCounter={openCounter}
              />
            </div>
          )}
        </AnimatePresence>
      </div>

      <Dialog
        onClose={handleDialogClose}
        open={isDialogOpen}
        PaperProps={{
          onMouseDown: (event) => {
            event.stopPropagation();
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            background: "white",
            top: 0,
            zIndex: 2,
            p: 2,
          }}
        >
          <DialogTitle>Confirm Cancel</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel this reservation?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                handleDialogClose();
              }}
            >
              No
            </Button>
            <Button variant="contained" onClick={handleDeclineConfirm}>
              Yes
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
}
